/**
 * Application root component that initializes routing, tRPC client,
 * React Query provider, and authentication state management.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import superjson from "superjson";

import { AppShell } from "@/components/layout/AppShell";
import { useIsAccountAdmin } from "@/hooks/usePermission";
import { trpc } from "@/lib/trpc";
import { AdminPage } from "@/pages/admin";
import { ModuleEditorPage } from "@/pages/admin/ModuleEditorPage";
import { Callback } from "@/pages/auth/Callback";
import { InvitePage } from "@/pages/auth/Invite";
import { Login } from "@/pages/auth/Login";
import { Dashboard } from "@/pages/Dashboard";
import { AccountSettingsPage } from "@/pages/settings/AccountSettings";
import { ModuleManagementPage } from "@/pages/settings/ModuleManagement";
import { useAuthStore } from "@/stores/auth";

/**
 * Auth initializer component
 * Attempts to restore session from cookies on app load
 * @param props - component props
 * @param props.children - child components to render
 */
function AuthInitializer({ children }: { children: React.ReactNode }) {
	const location = useLocation();
	const { session, setSession, setUser, setTeams, setAccounts, setActiveTeam, setPermissions } = useAuthStore();
	const [isInitializing, setIsInitializing] = useState(true);

	// Skip initialization for auth callback and invite routes (they handle their own auth)
	const isAuthRoute = location.pathname === "/auth/callback" || location.pathname === "/invite";

	// Try to get session from cookies
	const sessionQuery = trpc.auth.getSession.useQuery(undefined, {
		enabled: !session && !isAuthRoute,
		retry: false,
		staleTime: Infinity, // Don't refetch on window focus
	});

	// Fetch user profile if session exists
	const meQuery = trpc.auth.me.useQuery(undefined, {
		enabled: !!sessionQuery.data && !session && !isAuthRoute,
		retry: false,
		staleTime: Infinity,
	});

	useEffect(() => {
		// Skip for callback route
		if (isAuthRoute) {
			setIsInitializing(false);
			return;
		}

		// Already have session in store
		if (session) {
			setIsInitializing(false);
			return;
		}

		// No session from cookies
		if (sessionQuery.data === null || sessionQuery.error) {
			setIsInitializing(false);
			return;
		}

		// Session found, waiting for user profile
		if (sessionQuery.data && !meQuery.data && !meQuery.error) {
			return; // Still loading
		}

		// Both session and user data available
		if (sessionQuery.data && meQuery.data) {
			setSession(sessionQuery.data);

			// API returns brands but we use teams internally
			const { brands: teams, accounts, permissions, ...user } = meQuery.data;
			setUser(user);
			setPermissions(permissions);

			setTeams(teams);
			setAccounts(accounts);

			// Restore active team from persisted storage or use first team
			const storedActiveTeam = useAuthStore.getState().activeTeam;
			const hasSavedTeam = storedActiveTeam && teams.some(t => t.id === storedActiveTeam.id);
			if (!hasSavedTeam && teams.length > 0 && teams[0]) {
				setActiveTeam(teams[0]);
			}

			setIsInitializing(false);
			return;
		}

		// User profile fetch failed
		if (meQuery.error) {
			setIsInitializing(false);
			return;
		}
	}, [
		session,
		sessionQuery.data,
		sessionQuery.error,
		meQuery.data,
		meQuery.error,
		isAuthRoute,
		setSession,
		setUser,
		setTeams,
		setAccounts,
		setActiveTeam,
		setPermissions,
	]);

	// Show loading state while initializing (except for callback route)
	if (isInitializing && !isAuthRoute) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-background">
				<div className="flex flex-col items-center space-y-4">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
					<p className="text-sm text-muted-foreground">Loading...</p>
				</div>
			</div>
		);
	}

	return <>{children}</>;
}

/**
 * Protected route wrapper
 * Redirects to login if not authenticated
 * @param props - component props
 * @param props.children - child components to render when authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
	const { session } = useAuthStore();

	if (!session) {
		return <Navigate to="/login" replace />;
	}

	return <>{children}</>;
}

/**
 * Super admin route wrapper
 * Redirects non-super-admin users to dashboard
 * @param props - component props
 * @param props.children - child components to render when authorized
 */
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
	const { session, user } = useAuthStore();

	if (!session) {
		return <Navigate to="/login" replace />;
	}

	if (user?.role !== "super_admin") {
		return <Navigate to="/dashboard" replace />;
	}

	return <>{children}</>;
}

/**
 * Account admin route wrapper
 * Redirects users without account admin permissions to dashboard
 * @param props - component props
 * @param props.children - child components to render when authorized
 */
function AccountAdminRoute({ children }: { children: React.ReactNode }) {
	const { session } = useAuthStore();
	const isAccountAdmin = useIsAccountAdmin();

	if (!session) {
		return <Navigate to="/login" replace />;
	}

	if (!isAccountAdmin) {
		return <Navigate to="/dashboard" replace />;
	}

	return <>{children}</>;
}

/**
 * Main application component
 */
export function App() {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						staleTime: 1000 * 60 * 5, // 5 minutes
						retry: 1,
					},
				},
			})
	);

	const [trpcClient] = useState(() =>
		trpc.createClient({
			links: [
				httpBatchLink({
					url: `${import.meta.env.VITE_API_URL ?? ""}/trpc`,
					transformer: superjson,
					headers() {
						// Get current account/team context from auth store
						const { activeTeam, accounts } = useAuthStore.getState();
						const headers: Record<string, string> = {};

						// Use activeTeam's account if available, otherwise first account
						const accountId = activeTeam?.account.id ?? accounts[0]?.id;
						if (accountId) {
							headers["X-Account-Id"] = accountId;
						}
						if (activeTeam?.id) {
							headers["X-Team-Id"] = activeTeam.id;
						}

						return headers;
					},
					fetch(url, options) {
						return fetch(url, {
							...options,
							credentials: "include",
						});
					},
				}),
			],
		})
	);

	return (
		<trpc.Provider client={trpcClient} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<BrowserRouter>
					<AuthInitializer>
						<Routes>
							{/* Public routes */}
							<Route path="/login" element={<Login />} />
							<Route path="/auth/callback" element={<Callback />} />
							<Route path="/invite" element={<InvitePage />} />

							{/* Protected routes with app shell */}
							<Route
								element={
									<ProtectedRoute>
										<AppShell />
									</ProtectedRoute>
								}
							>
								<Route path="/dashboard" element={<Dashboard />} />
								<Route
									path="/admin"
									element={
										<SuperAdminRoute>
											<AdminPage />
										</SuperAdminRoute>
									}
								/>
								<Route
									path="/admin/modules/:moduleId"
									element={
										<SuperAdminRoute>
											<ModuleEditorPage />
										</SuperAdminRoute>
									}
								/>
								<Route
									path="/settings/account"
									element={
										<AccountAdminRoute>
											<AccountSettingsPage />
										</AccountAdminRoute>
									}
								/>
								<Route
									path="/settings/users"
									element={<Navigate to="/settings/account?tab=users" replace />}
								/>
								<Route
									path="/settings/modules"
									element={
										<AccountAdminRoute>
											<ModuleManagementPage />
										</AccountAdminRoute>
									}
								/>
								{/* Add more module routes here */}
								<Route path="/" element={<Navigate to="/dashboard" replace />} />
							</Route>

							{/* Catch-all redirect */}
							<Route path="*" element={<Navigate to="/dashboard" replace />} />
						</Routes>
					</AuthInitializer>
				</BrowserRouter>
			</QueryClientProvider>
		</trpc.Provider>
	);
}
